import dayjs, { Dayjs } from 'dayjs';
import { FileType } from 'src/models/file';
import { formatDate } from 'src/helpers/formatting-utils';
import { isEmpty } from 'src/helpers/validation-utils';

export enum DynamicField {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  CURRENCY = 'CURRENCY',
  PHONENUMBER = 'PHONENUMBER',
  SELECT = 'SELECT',
  MULTISELECT = 'MULTISELECT',
  RADIOGROUP = 'RADIOGROUP',
  CHECKBOX = 'CHECKBOX',
  CHECKBOXLIST = 'CHECKBOXLIST',
  TEXTAREA = 'TEXTAREA',
  FILE = 'FILE',
  DATE = 'DATE',
  EMAIL = "EMAIL"
}

export interface IDynamicBaseField {
  type: DynamicField;
  name: string;
  title: string;
  required: boolean;
  disabled: boolean;
  value: any;
  customValidator?: (v: any) => string;
  hidden?: boolean;
  placeholder?: string;
  helperText?: string;
  dependsOn?: string;
  resetOnDependencyChange?: boolean;
  loadItems?: (dependencyValue?: any) => Promise<Record<string, string>>;
  customValidation?: (value: any, allValues: Record<string, any>) => string | null;
  onChange?: (value: any, allValues: Record<string, any>) => void;
}

export interface IDynamicTextField extends IDynamicBaseField {
  type: DynamicField.TEXT;
  regex?: RegExp;
  minChars?: number;
  maxChars?: number;
  inputType?: string;
}

export interface IDynamicTextAreaField extends IDynamicBaseField {
  type: DynamicField.TEXTAREA;
  regex?: RegExp;
  minChars?: number;
  maxChars?: number;
  rows?: number;
}

export interface IDynamicPhoneNumbeField extends IDynamicBaseField {
  type: DynamicField.PHONENUMBER;
  format?: string;
}

export interface IDynamicNumberField extends IDynamicBaseField {
  type: DynamicField.NUMBER;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
}

export interface IDynamicCurrencyField extends IDynamicBaseField {
  type: DynamicField.CURRENCY;
  min?: number;
  max?: number;
  currencySymbol?: string;
}

export interface IDynamicSelectField extends IDynamicBaseField {
  type: DynamicField.SELECT;
  minOptions?: number;
  maxOptions?: number;
  items: Record<string, string>;
  value: string;
  clearable?: boolean;
}

export interface IDynamicMultiSelectField extends IDynamicBaseField {
  type: DynamicField.MULTISELECT;
  minOptions?: number;
  maxOptions?: number;
  items: Record<string, string>;
  value: string[];
}

export interface IDynamicRadioGroupField extends IDynamicBaseField {
  type: DynamicField.RADIOGROUP;
  items: Record<string, string>;
  value: string;
}

export interface IDynamicCheckboxField extends IDynamicBaseField {
  type: DynamicField.CHECKBOX;
  value: boolean;
}

export interface IDynamicCheckboxListField extends IDynamicBaseField {
  type: DynamicField.CHECKBOXLIST;
  minOptions?: number;
  maxOptions?: number;
  items: Record<string, string>;
  value: string[];
}

export interface IDynamicFileField extends IDynamicBaseField {
  value: string | File;
  maxSizeInMbs?: number;
  allowedTypes: FileType[];
  type: DynamicField.FILE;
  multiple?: boolean;
}

export interface IDynamiDatePickerField extends IDynamicBaseField {
  type: DynamicField.DATE;
  min?: Date | string;
  max?: Date | string;
  format?: string;
}

export interface IDynamicEmailField extends IDynamicBaseField {
  type: DynamicField.EMAIL;
}

export type DynamicFieldTypes =
  | IDynamicTextField
  | IDynamicNumberField
  | IDynamicCurrencyField
  | IDynamicTextAreaField
  | IDynamicSelectField
  | IDynamicMultiSelectField
  | IDynamicRadioGroupField
  | IDynamicCheckboxField
  | IDynamicCheckboxListField
  | IDynamicPhoneNumbeField
  | IDynamicFileField
  | IDynamiDatePickerField
  | IDynamicEmailField;

// Enhanced form state interface
export interface IDynamicFormState {
  values: Record<string, any>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  loading: Record<string, boolean>;
  dependencies: Record<string, string[]>;
}

export const prepareValuesForSubmission = (data: Record<string, any>, fields: Record<string, DynamicFieldTypes>): Record<string, any> => {
  var result: Record<string, any> = {};
  
  Object.keys(fields).forEach(k => {
    const field = fields[k];
    const value = data[k];
    
    // Skip hidden fields
    if (field.hidden) return;
    
    switch (field.type) {
      case DynamicField.DATE:
        if (!isEmpty(value) && dayjs(value).isValid()) {
          result[k] = formatDate(value, "YYYY-MM-DD");
        } else {
          result[k] = null;
        }
        break;
      
      case DynamicField.NUMBER:
      case DynamicField.CURRENCY:
        result[k] = value !== '' && value !== null && value !== undefined ? Number(value) : null;
        break;
      
      case DynamicField.FILE:
        // Files are typically handled separately
        result[k] = value;
        break;
      
      case DynamicField.CHECKBOX:
        result[k] = Boolean(value);
        break;
      
      case DynamicField.MULTISELECT:
      case DynamicField.CHECKBOXLIST:
        result[k] = Array.isArray(value) ? value : [];
        break;
      
      default:
        result[k] = value;
    }
  });
  
  return result;
};